import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { api } from "./ApiClient";

import { AuthHelper } from "../helpers/auth.helper";
import { HMICompleteConversation, HMIGenerateChatResponse, HMISaveFeedback, HMISaveUserMessage, HMIStartMBTSConversation, IConversationForAnalysisReport, IGenerateAudioResponsePayload, IGenerateAudioResponseResult, IMBTSPersonaWithChildren, IMBTSScenarioWithPersonasAndStatuses, IMBTSScenarioWithStatus, IMSARMBTSReport, ISynthesizeAudioPayload, ISynthesizeAudioResult } from "../types/services.interfaces/mbts-ux";
import { EtyMBTSConversation, EtyMBTSChat } from "../types/entities.interface";

export const useGetScenarios = () => {
    const enabled = AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<IMBTSScenarioWithStatus[]> => {
        const res = await api.get(`/mbts-ux/scenarios`);
        return res.data?.length ? res.data : [];
    };

    return useQuery({
        queryKey: ['mbts-scenarios'],
        queryFn,
        retry: false,
        enabled,
    });
};

export const useGetScenarioWithPersonas = (scenarioId: string | null) => {
    const enabled = AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<IMBTSScenarioWithPersonasAndStatuses> => {
        const res = await api.get(`/mbts-ux/scenario/${scenarioId}/personas`);
        return res.data;
    };

    return useQuery({
        queryKey: ['mbts-scenario-with-personas', scenarioId],
        queryFn,
        retry: false,
        enabled: enabled && !!scenarioId,
    });
};

export const useGetPersonaDetails = (scenarioId: string | null, personaId: string | null) => {
    const enabled = AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<IMBTSPersonaWithChildren> => {
        const res = await api.get(`/mbts-ux/persona/${scenarioId}/${personaId}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['mbts-persona-details', scenarioId, personaId],
        queryFn,
        retry: false,
        enabled: enabled && !!scenarioId && !!personaId,
    });
};

export const useStartConversation = () => {
    const navigate = useNavigate();

    AuthHelper.presetPrivateRequest(api);

    const mutationFn = async (data: HMIStartMBTSConversation): Promise<EtyMBTSConversation> => {
        const res = await api.post('/mbts-ux/conversation/start', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
            variables.setApiError(null);
        },
        onSuccess: (data, variables) => {
            navigate(`/mbts/${variables.scenarioId}/persona/${variables.payload.persona_id}/conversation/${data.id}`);
        },
        onError: (error: any, variables) => {
            variables.setApiError(error?.response?.data?.message || 'Something went wrong. Please try again.');
        },
        onSettled: (_, __, variables) => {
            variables.setIsProcessing(false);
        },
    });
};

export const useGetConversation = (conversationId: string | null) => {
    const enabled = AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<EtyMBTSConversation> => {
        const res = await api.get(`/mbts-ux/conversation/${conversationId}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['mbts-conversation', conversationId],
        queryFn,
        retry: false,
        enabled: enabled && !!conversationId,
    });
};

export const useGetChats = (conversationId: string | null) => {
    const enabled = AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<EtyMBTSChat[]> => {
        const res = await api.get(`/mbts-ux/chats/${conversationId}`);
        return res.data?.length ? res.data : [];
    };

    return useQuery({
        queryKey: ['mbts-chats', conversationId],
        queryFn,
        retry: false,
        enabled: enabled && !!conversationId,
    });
};

export const useSaveUserMessage = () => {
    const { mutate: generateChatResponse } = useGenerateChatResponse();

    AuthHelper.presetPrivateRequest(api);

    const mutationFn = async (data: HMISaveUserMessage): Promise<EtyMBTSChat> => {
        const res = await api.post('/mbts-ux/chat/save-user-message', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsSending(true);
        },
        onSuccess: (data, variables) => {
            variables.setChats(prev => [...prev, {
                by: data.source,
                message: data.message,
                timestamp: data.created_at,
            }]);
            variables.setMessage('');
            variables.setIsTyping(false);

            variables.setBottomScroll();

            generateChatResponse({
                conversationId: variables.payload.conversation_id,
                setIsGeneratingResponse: variables.setIsGeneratingResponse,
                chats: variables.chats,
                setChats: variables.setChats,
                setBottomScroll: variables.setBottomScroll,
                evaluateMessageThreshold: variables.evaluateMessageThreshold,
            });
        },
        onError: (error: any) => {
            window.alert(error?.response?.data?.message || 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setIsSending(false);
        },
    });
};

export const useGenerateChatResponse = () => {
    AuthHelper.presetPrivateRequest(api);

    const mutationFn = async (data: HMIGenerateChatResponse): Promise<EtyMBTSChat> => {
        const res = await api.post(`/mbts-ux/chat/generate-response/${data.conversationId}`);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsGeneratingResponse(true);
        },
        onSuccess: (data, variables) => {
            const newChat = {
                by: data.source,
                message: data.message,
                timestamp: data.created_at,
                typingEffect: true,
            };
            const newChats = [...variables.chats, newChat];
            variables.setChats(prev => [...prev, newChat]);

            variables.setBottomScroll();
            variables.evaluateMessageThreshold(newChats);
        },
        onError: (error: any) => {
            window.alert(error?.response?.data?.message || 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setIsGeneratingResponse(false);
        },
    });
};

export const useGenerateAudioResponse = () => {
    AuthHelper.presetPrivateRequest(api);

    const mutationFn = async (payload: IGenerateAudioResponsePayload): Promise<IGenerateAudioResponseResult> => {
        const res = await api.post('/mbts-ux/audio/generate-response', payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onError: (error: any) => {
            window.alert(error?.response?.data?.message || 'Something went wrong.');
        },
    });
};

export const useSynthesizeAudio = () => {
    AuthHelper.presetPrivateRequest(api);

    const mutationFn = async (payload: ISynthesizeAudioPayload): Promise<ISynthesizeAudioResult> => {
        const res = await api.post('/mbts-ux/audio/synthesize', payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onError: (error: any) => {
            window.alert(error?.response?.data?.message || 'Something went wrong.');
        },
    });
};

export const useCompleteConversation = () => {
    const navigate = useNavigate();

    AuthHelper.presetPrivateRequest(api);

    const mutationFn = async (data: HMICompleteConversation): Promise<EtyMBTSChat> => {
        const res = await api.patch(`/mbts-ux/conversation/complete/${data.conversationId}`);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: (_, variables) => {
            const { scenarioId, nextOperation, nextPersonaDetails } = variables;

            if (nextOperation === 'next-persona') {
                if (nextPersonaDetails) {
                    navigate(`/mbts/${scenarioId}/persona/${nextPersonaDetails.personaId}/details`)
                } else {
                    navigate(`/mbts/${scenarioId}/personas`);
                };
            } else {
                navigate(`/mbts/${scenarioId}/analysis`);
            };
        },
        onError: (error: any) => {
            window.alert(error?.response?.data?.message || 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setIsProcessing(false);
        },
    });
};

export const useGetAnalysis = (scenarioId: string | null) => {
    const enabled = AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<IMSARMBTSReport> => {
        const res = await api.post(`/mbts-ux/analysis/${scenarioId}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['mbts-analysis-report', scenarioId],
        queryFn,
        retry: false,
        enabled: enabled && !!scenarioId,
    });
};

export const useGetConversationsForAnalysisReport = (scenarioId: string | null, analysisId: number | null) => {
    const enabled = AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<IConversationForAnalysisReport[]> => {
        const res = await api.get(`/mbts-ux/conversations/${scenarioId}/${analysisId}`);
        return res.data?.length ? res.data : [];
    };

    return useQuery({
        queryKey: ['mbts-convos-for-report', scenarioId, analysisId],
        queryFn,
        retry: false,
        enabled: enabled && !!scenarioId && !!analysisId,
    });
};

export const useSaveFeedback = () => {
    AuthHelper.presetPrivateRequest(api);

    const mutationFn = async (data: HMISaveFeedback): Promise<void> => {
        const res = await api.post(`/mbts-ux/feedback`, data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: (_, variables) => {
            variables.setIsSuccess(true);
        },
        onError: (error: any) => {
            window.alert(error?.response?.data?.message || 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setIsProcessing(false);
        },
    });
};